import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getTodayRateThunk,
  getRateDashboardThunk,
} from "../../store/thunks/rateThunks";
import { RefreshIcon } from "@heroicons/react/outline";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

const HeaderRateWidget = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastFetchTimeRef = useRef(0);
  const initialFetchDoneRef = useRef(false);

  // Get rate data from Redux store
  const {
    dashboard,
    dailyRates,
    loading,
    ui: { selectedBranch },
  } = useSelector((state) => state.rate);

  const { user } = useSelector((state) => state.auth);

  // Get the current branch (from selected branch or user's primary branch)
  const currentBranch =
    selectedBranch || user?.branchAccess?.primaryBranch || "";

  // Initial fetch when component mounts or when branch changes
  useEffect(() => {
    if (currentBranch && !initialFetchDoneRef.current) {
      dispatch(getRateDashboardThunk({ branchCode: currentBranch }));
      dispatch(getTodayRateThunk({ branchCode: currentBranch }));
      lastFetchTimeRef.current = Date.now();
      initialFetchDoneRef.current = true;
    }
  }, [dispatch, currentBranch]);

  // Set up refresh interval
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      const now = Date.now();
      if (currentBranch && now - lastFetchTimeRef.current > 5 * 60 * 1000) {
        dispatch(getRateDashboardThunk({ branchCode: currentBranch }));
        lastFetchTimeRef.current = now;
      }
    }, 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [dispatch, currentBranch]);

  // Reset when branch changes
  useEffect(() => {
    initialFetchDoneRef.current = false;
  }, [currentBranch]);

  const handleRefreshRates = (e) => {
    e.stopPropagation();
    if (currentBranch) {
      dispatch(getRateDashboardThunk({ branchCode: currentBranch }));
      lastFetchTimeRef.current = Date.now();
    }
  };

  const navigateToRates = () => {
    navigate("/rates");
  };

  // Format rate with proper currency symbol and number formatting
  const formatRate = (rate, compact = false) => {
    if (!rate && rate !== 0) return "—";
    const numRate = parseFloat(rate);

    // For mobile, use compact format for large numbers
    if (compact && numRate >= 1000) {
      return `₹${(numRate / 1000).toFixed(1)}k`;
    }

    return `₹${numRate.toLocaleString("en-IN")}`;
  };

  // First try to get rates from dashboard, which is the primary source
  const dashboardRates =
    dashboard?.metalRates?.[currentBranch] || dashboard?.metalRates;

  // Access the branch-specific daily rate data as fallback
  const branchRateData = dailyRates?.byBranch?.[currentBranch];
  const isFallback = branchRateData?.isFallback;
  const isLoading = loading?.dashboard || loading?.dailyRates;

  // Prefer dashboard rates over daily rates
  const goldRate =
    dashboardRates?.GOLD?.baseRate ||
    branchRateData?.today?.rates?.GOLD?.baseRate;
  const silverRate =
    dashboardRates?.SILVER?.baseRate ||
    branchRateData?.today?.rates?.SILVER?.baseRate;
  const platinumRate =
    dashboardRates?.PLATINUM?.baseRate ||
    branchRateData?.today?.rates?.PLATINUM?.baseRate;

  const hasAnyRate =
    goldRate != null || silverRate != null || platinumRate != null;

  // Get timestamp, preferring dashboard data
  const dashboardTimestamp = dashboard?.lastUpdated;
  const dailyRateTimestamp =
    branchRateData?.today?.updatedAt ||
    branchRateData?.today?.publishedAt ||
    branchRateData?.today?.rateDate;
  const rateTimestamp = dashboardTimestamp || dailyRateTimestamp;

  // Format date for display
  const getFormattedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Determine if using dashboard or fallback data
  const isUsingDailyRateFallback = !dashboardRates && branchRateData?.today;
  const shouldShowFallbackIndicator = isFallback || isUsingDailyRateFallback;

  return (
    <div className="flex items-center gap-1 sm:gap-1.5">
      <style>
        {`
          /* Remove Tippy's default background and styling */
          .tippy-box[data-theme~='rate-widget'] {
            background-color: transparent;
            border: none;
            box-shadow: none;
            padding: 0;
          }
          
          .tippy-box[data-theme~='rate-widget'] .tippy-content {
            padding: 0;
          }
          
          .tippy-box[data-theme~='rate-widget'] .tippy-arrow {
            color: var(--color-neutral-dark);
          }
        `}
      </style>

      <Tippy
        content={
          <div className="bg-gradient-to-br from-neutral-dark to-glass-dark rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-2xl border border-neutral-dark/30 w-[260px] sm:min-w-[280px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-neutral-dark/50">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="relative w-1.5 h-1.5 sm:w-2 sm:h-2">
                  <div className="absolute inset-0 bg-success rounded-full animate-ping opacity-75"></div>
                  <div className="relative w-1.5 h-1.5 sm:w-2 sm:h-2 bg-success rounded-full"></div>
                </div>
                <span className="font-semibold text-text-on-primary text-sm sm:text-base">
                  Daily Rates
                </span>
              </div>
              {shouldShowFallbackIndicator && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-warning/20 text-warning text-[10px] sm:text-xs rounded-full border border-warning/30">
                  Fallback
                </span>
              )}
            </div>

            {/* Timestamp */}
            {rateTimestamp && (
              <div className="text-[10px] sm:text-xs text-text-on-primary/60 mb-2 sm:mb-3 flex items-center gap-1 sm:gap-1.5">
                <svg
                  className="w-3 h-3 sm:w-3.5 sm:h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Updated {getFormattedDate(rateTimestamp)}</span>
              </div>
            )}

            {/* Rates */}
            {hasAnyRate ? (
              <div className="space-y-2 sm:space-y-2.5">
                {goldRate != null && (
                  <div className="flex items-center justify-between p-2 sm:p-3 bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-transparent rounded-lg border border-yellow-500/20 hover:border-yellow-500/40 hover:bg-yellow-500/15 transition-all duration-300">
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center shadow-md">
                        <svg
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" />
                        </svg>
                      </div>
                      <span className="text-yellow-400 font-medium text-xs sm:text-sm">
                        Gold
                      </span>
                    </div>
                    <span className="text-text-on-primary font-semibold text-sm sm:text-base">
                      {formatRate(goldRate)}
                    </span>
                  </div>
                )}

                {silverRate != null && (
                  <div className="flex items-center justify-between p-2 sm:p-3 bg-gradient-to-r from-gray-400/10 via-gray-400/5 to-transparent rounded-lg border border-gray-400/20 hover:border-gray-400/40 hover:bg-gray-400/15 transition-all duration-300">
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-gray-300 to-gray-500 rounded-lg flex items-center justify-center shadow-md">
                        <svg
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" />
                        </svg>
                      </div>
                      <span className="text-gray-300 font-medium text-xs sm:text-sm">
                        Silver
                      </span>
                    </div>
                    <span className="text-text-on-primary font-semibold text-sm sm:text-base">
                      {formatRate(silverRate)}
                    </span>
                  </div>
                )}

                {platinumRate != null && (
                  <div className="flex items-center justify-between p-2 sm:p-3 bg-gradient-to-r from-purple-400/10 via-purple-400/5 to-transparent rounded-lg border border-purple-400/20 hover:border-purple-400/40 hover:bg-purple-400/15 transition-all duration-300">
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                        <svg
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" />
                        </svg>
                      </div>
                      <span className="text-purple-300 font-medium text-xs sm:text-sm">
                        Platinum
                      </span>
                    </div>
                    <span className="text-text-on-primary font-semibold text-sm sm:text-base">
                      {formatRate(platinumRate)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 sm:py-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-dark/50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-text-on-primary/40"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-text-on-primary/60 text-xs sm:text-sm">
                  No rates available
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-neutral-dark/50">
              <div className="text-[10px] sm:text-xs text-center text-text-on-primary/60 flex items-center justify-center gap-1 sm:gap-1.5">
                <svg
                  className="w-3 h-3 sm:w-3.5 sm:h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                  />
                </svg>
                <span className="hidden sm:inline">
                  Click to view full dashboard
                </span>
                <span className="sm:hidden">Tap for dashboard</span>
              </div>
            </div>
          </div>
        }
        placement="bottom"
        interactive={true}
        delay={[200, 0]}
        animation="scale"
        theme="rate-widget"
        touch={["hold", 500]}
        hideOnClick={true}
      >
        <div
          onClick={navigateToRates}
          className="group relative flex items-center 
            bg-glass-dark backdrop-blur-md rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 
            hover:bg-primary-dark/50 active:bg-primary-dark/60
            transition-all duration-300 cursor-pointer
            border border-neutral-light/10 hover:border-primary-light/30
            shadow-lg hover:shadow-xl
            touch-manipulation"
        >
          <div className="relative z-6">
            {isLoading ? (
              <div className="flex space-x-2 sm:space-x-3 px-0.5 sm:px-1">
                <div className="space-y-1 sm:space-y-1.5">
                  <div className="h-2.5 w-16 sm:h-3 sm:w-20 bg-gradient-to-r from-neutral-light/20 to-neutral-light/10 rounded animate-pulse"></div>
                  <div className="h-2 w-12 sm:w-16 bg-gradient-to-r from-neutral-light/15 to-neutral-light/5 rounded animate-pulse"></div>
                </div>
                <div className="space-y-1 sm:space-y-1.5">
                  <div className="h-2.5 w-16 sm:h-3 sm:w-20 bg-gradient-to-r from-neutral-light/20 to-neutral-light/10 rounded animate-pulse"></div>
                  <div className="h-2 w-12 sm:w-16 bg-gradient-to-r from-neutral-light/15 to-neutral-light/5 rounded animate-pulse"></div>
                </div>
              </div>
            ) : hasAnyRate ? (
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                {goldRate != null && (
                  <div className="flex flex-col" title="Gold Rate">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[9px] sm:text-[10px] font-medium text-yellow-400 uppercase tracking-wide">
                        Gold
                      </span>
                      <div className="relative w-1 h-1">
                        <div className="absolute inset-0 bg-yellow-400 rounded-full animate-ping"></div>
                        <div className="relative w-1 h-1 bg-yellow-400 rounded-full"></div>
                      </div>
                    </div>
                    <span className="text-xs sm:text-base font-semibold text-text-on-primary whitespace-nowrap">
                      {formatRate(goldRate, true)}
                    </span>
                  </div>
                )}

                {silverRate != null && (
                  <div className="flex flex-col" title="Silver Rate">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[9px] sm:text-[10px] font-medium text-gray-300 uppercase tracking-wide">
                        Silver
                      </span>
                      <div className="relative w-1 h-1">
                        <div className="absolute inset-0 bg-gray-300 rounded-full animate-ping"></div>
                        <div className="relative w-1 h-1 bg-gray-300 rounded-full"></div>
                      </div>
                    </div>
                    <span className="text-xs sm:text-base font-semibold text-text-on-primary whitespace-nowrap">
                      {formatRate(silverRate, true)}
                    </span>
                  </div>
                )}

                {/* Hide Platinum on very small screens */}
                {platinumRate != null && (
                  <div
                    className="hidden sm:flex flex-col"
                    title="Platinum Rate"
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[9px] sm:text-[10px] font-medium text-purple-300 uppercase tracking-wide">
                        Platinum
                      </span>
                      <div className="relative w-1 h-1">
                        <div className="absolute inset-0 bg-purple-300 rounded-full animate-ping"></div>
                        <div className="relative w-1 h-1 bg-purple-300 rounded-full"></div>
                      </div>
                    </div>
                    <span className="text-xs sm:text-base font-semibold text-text-on-primary whitespace-nowrap">
                      {formatRate(platinumRate, true)}
                    </span>
                  </div>
                )}

                {shouldShowFallbackIndicator && (
                  <div className="flex items-center">
                    <svg
                      className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-warning"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <svg
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-text-on-primary/50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-xs sm:text-sm font-medium text-text-on-primary/70">
                  Set rates
                </span>
              </div>
            )}
          </div>
        </div>
      </Tippy>

      {/* Refresh Button */}
      <button
        onClick={handleRefreshRates}
        disabled={isLoading}
        className="relative group px-1 py-1 sm:px-2 sm:py-2
          bg-glass-dark backdrop-blur-md
          hover:bg-primary-dark/50 active:bg-primary-dark/60
          rounded-lg sm:rounded-xl transition-all duration-300 
          border border-neutral-light/10 hover:border-primary-light/30
          shadow-lg hover:shadow-xl
          disabled:opacity-50 disabled:cursor-not-allowed
          touch-manipulation
          self-stretch"
        title="Refresh rates"
      >
        <RefreshIcon
          className={`h-3.5 w-3.5 sm:h-4 sm:w-4 text-text-on-primary transition-transform duration-500 ${
            isLoading ? "animate-spin" : "group-hover:rotate-180"
          }`}
        />

        {/* Ripple effect on click */}
        <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-primary-light/20 scale-0 group-active:scale-100 transition-transform duration-200"></div>
      </button>
    </div>
  );
};

export default HeaderRateWidget;
